import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { routerWithQueryClient } from '@tanstack/react-router-with-query';
import { initMonitoring } from '@repo/monitoring';
import {
  handleSessionExpired,
  isSessionExpiredError,
  SESSION_EXPIRED_ERROR_NAME,
} from '@repo/api-client';
import { ENVIRONMENT, SENTRY_DSN } from '~/config/env';
import { routeTree } from './routeTree.gen';

// Initialize monitoring with universal error rules
initMonitoring({
  dsn: SENTRY_DSN,
  environment: ENVIRONMENT,

  // To test error reporting in sandbox/development, uncomment:
  // captureErrors: true,

  errorRules: {
    captureOn5xx: true,
    captureOn4xx: false,
    captureNetworkErrors: false,
    captureTimeouts: true,
  },
  sentryOptions: {
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
    ignoreErrors: [SESSION_EXPIRED_ERROR_NAME],
  },
});

export function createRouter() {
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: handleSessionExpired,
    }),
    mutationCache: new MutationCache({
      onError: handleSessionExpired,
    }),
    defaultOptions: {
      queries: {
        retry: (failureCount, error: any) => {
          if (isSessionExpiredError(error)) {
            return false;
          }
          // Don't retry on network errors (offline)
          if (!error?.response && error?.message?.includes('Network Error')) {
            return false;
          }
          // Retry other errors up to 3 times
          return failureCount < 3;
        },
        // useSuspenseQuery already throws errors by default
        // They will be caught by Suspense error boundaries
      },
      // Mutations should handle errors locally (toasts, form errors, etc.)
      // Don't throw to error boundary as it would break the entire page
    },
  });

  const router = routerWithQueryClient(
    createTanStackRouter({
      routeTree,
      context: { queryClient, isAuthenticated: false, user: null },
      defaultPreload: 'intent',
      // Removed defaultErrorComponent - let errors bubble up to AppErrorBoundary
      defaultNotFoundComponent: () => <>Not Found</>,
      scrollRestoration: true,
    }),
    queryClient,
  );

  return router;
}

export const router = createRouter();

export type KnownRoutes = typeof router.routesByPath;

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}
